/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { AuthoringRegion } from 'src/core/types/regionTypes';
import { LuisAppStore } from 'src/luis/core/stores/LuisAppStore';
import { luisAppSwaggerToLuisAppStore } from 'src/luis/modules/apps/handlers/luisAppMappers';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { getAzureMigrationService } from 'src/verticals/azureMigrationModal/services/azureMigrationService';
import { UserMigrationInfoStore } from 'src/verticals/azureMigrationModal/stores/UserMigrationInfoStore';

export type AzureMigrationHandlerGroup = ReturnType<typeof createAzureMigrationHandlerGroup>;

/**
 * Represents a handler group for migration operations.
 */
export const createAzureMigrationHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    /**
     * Migrate the user.
     *
     * @param subscriptionId The subscription id to migrate to.
     * @param resourceGroup The resource group to migrate to.
     * @param accountName The account name to migrate to.
     * @param accountKeyString The account key string to migrate to.
     */
    const migrate = async ({
        region,
        endpoint,
        serviceKey,
        accountName,
        subscriptionId,
        resourceGroupName
    }: HandlerParams<{
        endpoint: string;
        serviceKey: string;
        accountName: string;
        subscriptionId: string;
        region: AuthoringRegion;
        resourceGroupName: string;
    }>) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;

        await getAzureMigrationService().migrate({
            endpoint,
            serviceKey,
            tenantToken,
            accountName,
            resourceGroup: resourceGroupName,
            azureSubscriptionId: subscriptionId
        });

        rootStore.userMigrationInfoStoreHouse.items.find(item => item.region === region).setHasMigratedSuccessfully(true);
    };

    /**
     * Exports the app when it is called from the migration
     * modal, which first fetches the app to get its active
     * version, then exports it.
     *
     * @param param0.appId The id of the app to export
     * @param param0.region The region to get the app from.
     * @param param0.appName The name of the app used to name the file.
     * @param param0.serviceKey The service key used to download the file.
     */
    const exportAppFromMigrationModal = async ({
        appId,
        endpoint,
        appName,
        serviceKey
    }: HandlerParams<{ appId: string; appName: string; serviceKey: string; endpoint: string }>) => {
        await getAzureMigrationService().exportApp({ appId, serviceKey, endpoint, fileName: appName });
    };

    /**
     * Gets conversation apps belongs to the given service key.
     */
    const getAppsPerRegion = async ({
        userMigrationInfoStore,
        localStore
    }: HandlerParams<{ userMigrationInfoStore: UserMigrationInfoStore; localStore: ResourceStoreHouse<LuisAppStore> }>) => {
        localStore.setIsLoading(true);

        const conversationAppsSwaggers = await getAzureMigrationService().getConversationAppsPerRegion({ ...userMigrationInfoStore });
        const conversationAppsStores = conversationAppsSwaggers.map(luisAppSwaggerToLuisAppStore);

        localStore.setItems(conversationAppsStores);

        localStore.setIsLoading(false);
        localStore.setIsLoaded(true);
    };

    /**
     * Deletes user and its apps in the given region.
     */
    const deleteUserInRegion = async ({ userMigrationInfoStore }: HandlerParams<{ userMigrationInfoStore: UserMigrationInfoStore }>) => {
        await getAzureMigrationService().deleteUserInRegion({ ...userMigrationInfoStore });
        rootStore.userMigrationInfoStoreHouse.removeById(userMigrationInfoStore.id);
    };

    return { migrate, exportAppFromMigrationModal, getAppsPerRegion, deleteUserInRegion };
};
